
import React from "react";
import { cn } from "@/lib/utils";

interface LogoProps {
  variant?: "default" | "mark" | "wordmark";
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
  darkMode?: boolean;
}

const Logo = ({ 
  variant = "default", 
  size = "md", 
  className,
  darkMode = false
}: LogoProps) => {
  const sizes = {
    sm: "h-6",
    md: "h-8",
    lg: "h-10",
    xl: "h-12",
  };

  // Mark-only version (just the icon)
  if (variant === "mark") {
    return (
      <div className={cn("relative flex items-center justify-center", sizes[size], className)}>
        <svg 
          viewBox="0 0 40 40" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          className={cn("h-full w-auto", className)}
        >
          <circle 
            cx="20" 
            cy="20" 
            r="18" 
            className={darkMode ? "fill-white/10" : "fill-tech-blue/5"}
          />
          <path 
            d="M13 20C13 16.134 16.134 13 20 13C23.866 13 27 16.134 27 20C27 23.866 23.866 27 20 27" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            className={darkMode ? "stroke-white" : "stroke-tech-blue"}
          />
          <path 
            d="M20 27C20 27 16 27 12 31" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            className="stroke-tech-cyan"
          />
          <circle 
            cx="30" 
            cy="16" 
            r="2.5" 
            className="fill-tech-orange"
          />
        </svg>
      </div>
    );
  }

  // Word mark only (just the text)
  if (variant === "wordmark") {
    return (
      <div className={cn("font-['Montserrat'] font-bold", sizes[size], className)}>
        <span className={darkMode ? "text-white" : "text-tech-blue"}>Tech</span>
        <span className="text-tech-cyan">Ovation</span>
        <span className={darkMode ? "text-white/80" : "text-tech-blue/80"}>.io</span>
      </div>
    );
  }

  // Default is combined mark and text
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div className="relative">
        <svg 
          viewBox="0 0 40 40" 
          fill="none" 
          xmlns="http://www.w3.org/2000/svg"
          className={cn(sizes[size], "w-auto")}
        >
          <circle 
            cx="20" 
            cy="20" 
            r="18" 
            className={darkMode ? "fill-white/10" : "fill-tech-blue/5"}
          />
          <path 
            d="M13 20C13 16.134 16.134 13 20 13C23.866 13 27 16.134 27 20C27 23.866 23.866 27 20 27" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            className={darkMode ? "stroke-white" : "stroke-tech-blue"}
          />
          <path 
            d="M20 27C20 27 16 27 12 31" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            className="stroke-tech-cyan"
          />
          <circle 
            cx="30" 
            cy="16" 
            r="2.5" 
            className="fill-tech-orange"
          />
        </svg>
      </div>
      <div className={cn(
        "font-['Montserrat'] font-bold leading-none", 
        {
          "text-lg": size === "sm",
          "text-xl": size === "md",
          "text-2xl": size === "lg",
          "text-3xl": size === "xl",
        }
      )}>
        <span className={darkMode ? "text-white" : "text-tech-blue"}>Tech</span>
        <span className="text-tech-cyan">Ovation</span>
        <span className={darkMode ? "text-white/80" : "text-tech-blue/80"}>.io</span>
      </div>
    </div>
  );
};

export default Logo;
